import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { useFetch } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { PageTitle, Empty, inputCls, btnGhost } from "./ui";
import { BrandLoader } from "../components/site/Logo";

const CAT = { opci_upit: "Opći upit", dojava_vijesti: "Dojava vijesti", saradnja: "Saradnja i partnerstva", marketing: "Marketing i oglašavanje", ispravka: "Prijava greške", gegendarstellung: "Gegendarstellung" };
const ST = { new: "Novo", in_progress: "U obradi", waiting: "Čeka odgovor", resolved: "Riješeno", closed: "Zatvoreno", spam: "Spam" };

const Notes = ({ c, canManage, onAdded }) => {
  const [text, setText] = useState("");
  const add = async () => {
    try { await api.post(`/admin/contact/${c.id}/notes`, { text }); setText(""); onAdded(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2">
      {c.notes && <p className="text-xs text-muted-foreground">Starija bilješka: {c.notes}</p>}
      {(c.internal_notes || []).map((n) => <p key={n.id} className="rounded bg-muted/50 p-2 text-xs" data-testid={`inquiry-note-${n.id}`}><strong>{n.by}</strong> · {fmtDateTime(n.at)}<br />{n.text}</p>)}
      {canManage && <div className="flex gap-2"><input placeholder="Nova interna bilješka" value={text} onChange={(e) => setText(e.target.value)} className={inputCls} data-testid={`inquiry-note-input-${c.id}`} /><button disabled={text.trim().length < 2} onClick={add} className={btnGhost} data-testid={`inquiry-note-add-${c.id}`}>Dodaj</button></div>}
    </div>
  );
};

const Item = ({ c, staff, canManage, reload }) => {
  const { can } = useAuth();
  const nav = useNavigate();
  const toRegister = async () => {
    try { const { data } = await api.post(`/admin/corrections/from-contact/${c.id}`); nav(`/admin/ispravke?id=${data.id}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ status: c.status, assigned_to: c.assigned_to || "" });
  const save = async () => {
    try { await api.put(`/admin/contact/${c.id}`, f); toast.success("Upit ažuriran"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Trajno obrisati upit i lične podatke pošiljaoca?")) return;
    try { await api.delete(`/admin/contact/${c.id}`); toast.success("Upit obrisan"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <li className="rounded-lg border border-border bg-card" data-testid={`inquiry-${c.id}`}>
      <button onClick={() => setOpen(!open)} className="flex w-full flex-col gap-1 p-4 text-left sm:flex-row sm:items-center sm:justify-between" data-testid={`inquiry-toggle-${c.id}`}>
        <span><span className="mr-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase">{CAT[c.category]}</span><span className="font-medium">{c.subject}</span></span>
        <span className="text-xs text-muted-foreground">{c.name} · {ST[c.status]}{c.assigned_name && ` · ${c.assigned_name}`} · {fmtDateTime(c.created_at)}</span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-border p-4 text-sm">
          <p><a href={`mailto:${c.email}`} className="text-brandblue underline dark:text-sky-300">{c.email}</a>{c.article_url && <> · <a href={c.article_url} target="_blank" rel="noreferrer nofollow" className="underline">članak</a></>}</p>
          <p className="whitespace-pre-wrap rounded-md bg-muted/50 p-3" data-testid={`inquiry-message-${c.id}`}>{c.message}</p>
          <p className="text-xs text-muted-foreground">Email obavijest redakciji: {c.email_notification}. Odgovor pošiljaocu šaljete iz svog email programa – sistem ne šalje odgovore.</p>
          {canManage && (
            <div className="grid gap-2 sm:grid-cols-[170px_1fr_auto_auto]">
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inputCls} data-testid={`inquiry-status-${c.id}`}>{Object.entries(ST).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
              <select value={f.assigned_to} onChange={(e) => setF({ ...f, assigned_to: e.target.value })} className={inputCls} data-testid={`inquiry-assign-${c.id}`}><option value="">— nije dodijeljeno —</option>{staff.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
              <button onClick={save} className={btnGhost} data-testid={`inquiry-save-${c.id}`}>Sačuvaj</button>
              <button onClick={remove} className={`${btnGhost} text-destructive`} data-testid={`inquiry-delete-${c.id}`}>Obriši</button>
            </div>
          )}
          <Notes c={c} canManage={canManage} onAdded={reload} />
          {["ispravka", "gegendarstellung"].includes(c.category) && can("corrections.manage") && <button onClick={toRegister} className={btnGhost} data-testid={`inquiry-to-register-${c.id}`}>Otvori u registru ispravki</button>}
        </div>
      )}
    </li>
  );
};

export default function Inquiries() {
  const { can } = useAuth();
  const [flt, setFlt] = useState({ status: "", category: "", q: "", mine: false });
  const [tick, setTick] = useState(0);
  const [staff, setStaff] = useState([]);
  const canManage = can("contact.manage");
  useEffect(() => {
    if (canManage) api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
  }, [canManage]);
  const qs = new URLSearchParams(Object.entries(flt).filter(([, v]) => v).map(([k, v]) => [k, String(v)]));
  const { data, loading } = useFetch(`/admin/contact?${qs}`, [tick]);
  const reload = () => setTick((t) => t + 1);
  const purge = async () => {
    if (!window.confirm("Obrisati spam starije od 30 dana i riješene/zatvorene upite starije od 12 mjeseci?")) return;
    try { const { data: r } = await api.post("/admin/contact/purge"); toast.success(`Obrisano: spam ${r.spam}, zatvoreni ${r.closed}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-inquiries-page">
      <PageTitle title="Kontakt upiti" sub="Poruke s kontakt forme. Zadržavanje: spam 30 dana, riješeni upiti 12 mjeseci." actions={canManage && <button onClick={purge} className={btnGhost} data-testid="inquiries-purge">Primijeni pravila zadržavanja</button>} />
      <div className="mb-4 grid gap-2 sm:grid-cols-4">
        <input placeholder="Pretraga" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} className={inputCls} data-testid="inquiries-search" />
        <select value={flt.status} onChange={(e) => setFlt({ ...flt, status: e.target.value })} className={inputCls} data-testid="inquiries-status-filter"><option value="">Sve (bez spama)</option>{Object.entries(ST).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <select value={flt.category} onChange={(e) => setFlt({ ...flt, category: e.target.value })} className={inputCls} data-testid="inquiries-category-filter"><option value="">Sve kategorije</option>{Object.entries(CAT).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={flt.mine} onChange={(e) => setFlt({ ...flt, mine: e.target.checked })} data-testid="inquiries-mine" /> Dodijeljeno meni</label>
      </div>
      {loading && !data ? <BrandLoader /> : data?.length ? (
        <ul className="space-y-3" data-testid="inquiries-list">{data.map((c) => <Item key={c.id} c={c} staff={staff} canManage={canManage} reload={reload} />)}</ul>
      ) : <Empty testId="inquiries-empty">Nema upita.</Empty>}
    </div>
  );
}
